import React, { useLayoutEffect, useRef, useState } from 'react';

import MenuItem from '~/components/core/Atomic/MenuItem';
import type { SelectedToken, SuggestionOption } from '~/components/core/Editor/types';
import ChevronRightIcon from '~/components/icons/ChevronRightIcon';
import TextWithMdBold from '~/components/TextWithBold/TextWithMDBold';

import { sortSuggestionOptions } from './suggestion';

interface SuggestionMenuItemProps {
  label: string;
  id: string;
  subItems: undefined | SuggestionOption[];
  handleSelectItem: (token: SelectedToken) => void;
  depth?: number;
  parentPath?: string[];
  tags?: string[];
  /** The option this menu is currently showing the value of, when the menu is picking a *value*
   *  rather than inserting one. A token picker has no current value and passes nothing, which is
   *  why this is optional: without it every branch below behaves exactly as it did before. */
  selectedId?: string;
  /** Rendered after a leaf's label and tags, looked up by the leaf's id — the rules-engine field
   *  picker marks its collection fields this way. A group row never calls it, so the chevron column
   *  stays reserved for rows that expand. The token pickers pass nothing and render as before. */
  renderLeafAdornment?: (id: string) => React.ReactNode;
}

/** Whether `selectedId` is this subtree's own id or any descendant's.
 *
 *  Drives the initial expansion, which is the half that makes the selection reachable rather than
 *  merely marked: the tree renders collapsed, so a selected leaf two groups deep is not in the DOM
 *  at all until its ancestors open. Recursion rather than a flattened index because the caller
 *  hands this component one node at a time and each one only needs the answer for itself. */
const subtreeHasSelection = (id: string, subItems: undefined | SuggestionOption[], selectedId?: string): boolean => {
  if (selectedId === undefined) return false;
  if (id === selectedId) return true;
  return (subItems ?? []).some((item) => subtreeHasSelection(item.id, item.sub_items, selectedId));
};

const SuggestionMenuItem: React.FC<SuggestionMenuItemProps> = ({
  label,
  id,
  subItems,
  handleSelectItem,
  depth = 0,
  parentPath = [],
  tags,
  selectedId,
  renderLeafAdornment,
}) => {
  // Initial state, not an effect: a group holding the selection is open on its first render, so
  // the leaf is in the DOM for the scroll below to find. `useState`'s initialiser runs once, which
  // is the intent — the author collapsing a group must stay collapsed, so a later render must not
  // reopen it.
  const [isExpanded, setIsExpanded] = useState(() => subtreeHasSelection(id, subItems, selectedId));

  const hasChildren = subItems && subItems.length > 0;
  const isSelected = selectedId !== undefined && id === selectedId;
  const selectedRef = useRef<HTMLLIElement>(null);

  // Brings the selected leaf into view once its ancestors have expanded. Layout effect rather
  // than effect so it runs before paint and the menu never shows the top of the list first.
  // `block: 'nearest'` scrolls only when the leaf is actually out of view, so a selection already
  // on screen does not jump.
  //
  // The method is feature-checked, not just the ref: `scrollIntoView` is one of the DOM APIs jsdom
  // does not implement, so calling it unguarded throws in every test that renders a selected row —
  // which is how this was caught. Scrolling is a nicety on top of the `selected` marking, so the
  // right answer where it is unavailable is to skip it, not to stub it at each call site.
  useLayoutEffect(() => {
    if (isSelected) selectedRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [isSelected]);

  // Leaf item - clickable token
  if (!hasChildren) {
    return (
      <MenuItem
        key={id}
        ref={isSelected ? selectedRef : undefined}
        selected={isSelected}
        onClick={() => {
          handleSelectItem({ id, label, groupPath: parentPath.length > 0 ? parentPath : undefined });
        }}
        className="pl-12"
      >
        <TextWithMdBold text={label} />
        {tags?.map((tag) => (
          <span key={tag} className="ml-4 text-xs text-slate-700">
            ({tag})
          </span>
        ))}
        {renderLeafAdornment?.(id)}
      </MenuItem>
    );
  }

  // Group item with sub-items - accordion style
  const currentPath = [...parentPath, label];
  const sortedSubItems = sortSuggestionOptions(subItems);

  return (
    <div>
      <MenuItem key={id} onClick={() => setIsExpanded(!isExpanded)} className="flex items-center pl-12">
        <ChevronRightIcon
          className={`mr-12 text-slate-900 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          size={20}
        />
        <TextWithMdBold text={label} />
      </MenuItem>
      {isExpanded && (
        <div className="ml-20 border-0 border-l border-solid border-blue-400">
          {sortedSubItems.map((item) => (
            <SuggestionMenuItem
              key={item.id}
              id={item.id}
              label={item.label}
              subItems={item.sub_items}
              handleSelectItem={handleSelectItem}
              depth={depth + 1}
              parentPath={currentPath}
              tags={item.tags}
              selectedId={selectedId}
              renderLeafAdornment={renderLeafAdornment}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SuggestionMenuItem;
